import { useCallback, useEffect, useRef, useState } from "react";
import cloneDeep from "lodash/cloneDeep";

import { useDomainFormInfo } from "@/hooks/useDomainFormInfo";
import { useDomainProductInstanceId } from "@/hooks/useDomainProductInstanceId";
import {
  applyWebsitePrefillFromFormData,
  formDataToWebsiteDetailsPatch,
  mergeGettingStartedIntoDomainFormSnapshot,
} from "@/helpers/domainFormMerge";

/**
 * Resolves domain SFAM context, loads domain-form snapshot, prefills getting-started
 * fields when empty, and exposes POST after successful GET.
 *
 * @param {object} params
 * @param {string | null | undefined} params.projectsToken — projects JWT from onboarding context
 * @param {string | undefined} params.productInstanceId — `_sp` product instance id for SFAM auth
 * @param {React.Dispatch<React.SetStateAction<{ title: string; category: string; prompt: string }>>} params.setWebsiteDetails
 */
export function useGettingStartedDomainForm({
  projectsToken,
  productInstanceId,
  setWebsiteDetails,
}) {
  const domainFormLookup = useDomainProductInstanceId(projectsToken, {
    enabled: Boolean(projectsToken),
  });

  const {
    query: domainFormInfoQuery,
    mutation: domainFormInfoMutation,
  } = useDomainFormInfo(
    domainFormLookup.domainProductInstanceId,
    productInstanceId,
  );

  const [domainFormLastGet, setDomainFormLastGet] = useState(null);
  const domainFormPrefilledRef = useRef(false);

  const hasResolvableDomainForm = Boolean(
    domainFormLookup.domainProductInstanceId &&
      domainFormLookup.domainName,
  );

  const domainFormPersistReady = Boolean(
    hasResolvableDomainForm && domainFormLastGet != null,
  );

  useEffect(() => {
    domainFormPrefilledRef.current = false;
    setDomainFormLastGet(null);
  }, [
    domainFormLookup.domainProductInstanceId,
    domainFormLookup.domainName,
  ]);

  useEffect(() => {
    if (domainFormInfoQuery.isSuccess && domainFormInfoQuery.data) {
      setDomainFormLastGet(cloneDeep(domainFormInfoQuery.data));
    }
  }, [domainFormInfoQuery.isSuccess, domainFormInfoQuery.data]);

  useEffect(() => {
    if (domainFormInfoQuery.isError) {
      setDomainFormLastGet(null);
    }
  }, [domainFormInfoQuery.isError]);

  useEffect(() => {
    if (!domainFormLastGet || !hasResolvableDomainForm) return;
    if (domainFormPrefilledRef.current) return;
    const patch = formDataToWebsiteDetailsPatch(domainFormLastGet.formData);
    if (Object.keys(patch).length === 0) return;
    domainFormPrefilledRef.current = true;
    setWebsiteDetails((prev) => applyWebsitePrefillFromFormData(prev, patch));
  }, [domainFormLastGet, hasResolvableDomainForm, setWebsiteDetails]);

  const persistDomainFormAfterGettingStarted = useCallback(
    async (details) => {
      if (!domainFormPersistReady || !domainFormLastGet) {
        return;
      }
      const merged = mergeGettingStartedIntoDomainFormSnapshot(
        domainFormLastGet,
        details,
      );
      await domainFormInfoMutation.mutateAsync(merged);
    },
    [
      domainFormPersistReady,
      domainFormLastGet,
      domainFormInfoMutation,
    ],
  );

  return {
    domainFormLookup,
    domainFormInfoQuery,
    hasResolvableDomainForm,
    domainFormPersistReady,
    persistDomainFormAfterGettingStarted,
  };
}
